import * as Headless from '@headlessui/react';
import clsx from 'clsx';
import React from 'react';

import { Button } from '@/common/components/button';
import { SidebarProvider } from '@/common/components/sidebar/sidebar-provider';
import { useSidebar } from '@/common/components/sidebar/use-sidebar';
import { SidebarIcon } from '@/icons/fa/light';
import { cn } from '@/shared/classnames';

function MobileSidebar({ children }: { children: React.ReactNode }) {
  const { mobileIsOpen, setMobileIsOpen } = useSidebar();

  function closeMobileSidebar() {
    setMobileIsOpen(false);
  }

  return (
    <Headless.Dialog open={mobileIsOpen} onClose={closeMobileSidebar} className="lg:hidden">
      <Headless.DialogBackdrop
        transition
        className="fixed inset-0 bg-zinc-950/30 transition duration-200 ease-linear data-closed:opacity-0"
      />
      <Headless.DialogPanel
        transition
        className="fixed inset-y-0 w-full max-w-80 transition duration-200 ease-linear data-closed:-translate-x-full"
      >
        <div className="flex h-full flex-col bg-white ring-1 shadow-xs ring-zinc-950/5 dark:bg-zinc-900 dark:ring-zinc-700">
          {children}
        </div>
      </Headless.DialogPanel>
    </Headless.Dialog>
  );
}

type SidebarTriggerProps = {
  className?: string;
  onClick?: (event: any) => void;
};

export function SidebarTrigger({
  className,
  onClick
}: SidebarTriggerProps) {
  const { toggle } = useSidebar();

  function handleClick(event: any) {
    onClick?.(event);
    console.log('clicked');
    toggle();
  }

  return (
    <Button className={cn(className)} onClick={handleClick} plain={true}>
      <SidebarIcon />
      <span className="sr-only">Toggle Sidebar</span>
    </Button>
  );
}

export function MobileNavbar({ children }: { children: React.ReactNode }) {
  return (
    <header className="flex items-center px-4 lg:hidden">
      <div className="py-2.5">
        <SidebarTrigger className="-ml-1" />
      </div>
      <div className="min-w-0 flex-1">{children}</div>
    </header>
  );
}

export function SidebarLayout({
  navbar,
  sidebar,
  children
}: React.PropsWithChildren<{ navbar?: React.ReactNode; sidebar: React.ReactNode }>) {
  return (
    <SidebarProvider>
      <div className="relative isolate flex min-h-svh w-full bg-white max-lg:flex-col lg:bg-zinc-100 dark:bg-zinc-900 dark:lg:bg-zinc-950">
        {/* Sidebar on desktop */}
        <DesktopSidebar>{sidebar}</DesktopSidebar>

        {/* Sidebar on mobile */}
        <MobileSidebar>{sidebar}</MobileSidebar>

        {/* Content */}
        <SidebarLayoutMain navbar={navbar}>{children}</SidebarLayoutMain>
      </div>
    </SidebarProvider>
  );
}

export function DesktopSidebar({ className, ...props }: React.ComponentProps<'div'>) {
  const { isOpen, state } = useSidebar();

  return (
    <div
      data-state={state}
      className={cn(
        'group fixed inset-y-0 left-0 max-lg:hidden transition-[width] duration-200 ease-linear',
        isOpen ? 'w-64' : 'w-16'
      )}
      {...props}
    />
  );
}

function SidebarLayoutMain({ children, navbar }: { children: React.ReactNode; navbar?: React.ReactNode }) {
  const { isOpen } = useSidebar();

  return (
    <main className={
      clsx(
        'flex flex-1 flex-col lg:min-w-0 transition-[padding] duration-200 ease-linear',
        isOpen ? 'lg:pl-64' : 'lg:pl-16'
      )
    }
    >
      <div className="flex flex-col grow p-3 lg:bg-white lg:ring-1 lg:shadow-xs lg:ring-zinc-950/5 dark:lg:bg-zinc-800 dark:lg:ring-zinc-700">
        {navbar && (
          <div className="-mt-2.5">{navbar}</div>
        )}
        <div className="w-full mx-auto max-w-10xl">
          {children}
        </div>
      </div>
    </main>
  );
}
